import {AppActions, useAppContext} from "../context/AppContext";
import {useEffect, useRef, useState} from "react";
import {Button, Modal, notification, Select, Tabs, Tag, Tooltip} from "antd";
import HecRasProject from "./HecRasProject";
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    CloseOutlined,
    ExclamationCircleOutlined,
    FilterOutlined,
    InfoCircleOutlined,
    UndoOutlined
} from "@ant-design/icons";
import {PlanProfiles} from "./ProjectUploader";
import ApiService from "../services/ApiService";
import JSZip from "jszip";
import ResultTable from "../components/table/ResultTable";


const ProjectTab = ({project, onProjectChanged, onSaveProject, allProjects}) => {
    return (
        <HecRasProject
            project={project}
            onProjectChanged={onProjectChanged}
            onSaveProject={onSaveProject}
            allProjects={allProjects}
        />
    )
}

const compressJsonBase64 = async (json) => {
    const zip = new JSZip();
    zip.file('data.json', JSON.stringify(json));
    return await zip.generateAsync({ type: 'base64', compression: 'DEFLATE', compressionOptions: {level: 9} });
}

// Mirrors the red-cell rules in HecRasProject.renderOutputCol: a plan row is
// flagged when its profile data is missing or the plan isn't found in the
// output file.
const hasOutputError = (project) => {
    return project?.projectFiles?.some(pf =>
        !pf?.profileData ||
        Object.keys(pf.profileData).length === 0 ||
        pf?.isFindOutput === 'false'
    )
}

const TH_STYLE = {
    border: '1px solid #e0e0e0',
    backgroundColor: '#f0f2f5',
    padding: '2px 6px',
    fontWeight: 'bold',
    textAlign: 'left',
    whiteSpace: 'nowrap',
}

const TD_STYLE = {
    border: '1px solid #e0e0e0',
    padding: '2px 6px',
    verticalAlign: 'top',
}

const HiddenItemsModal = ({open, onClose, projects, filteredOutProjects, profile}) => {
    const rows = []
    for (const p of (projects || [])) {
        for (const pf of (p?.filteredOutFiles || [])) {
            rows.push({
                project: `${p?.information?.folderName || ''} / ${p?.information?.title || ''}`,
                model: p?.modelType || '',
                plan: pf?.plan?.title || pf?.planTitle || '',
                reason: 'Plan hidden'
            })
        }
    }
    for (const p of (filteredOutProjects || [])) {
        const allPlans = [...(p?.projectFiles || []), ...(p?.filteredOutFiles || [])]
        for (const pf of allPlans) {
            rows.push({
                project: `${p?.information?.folderName || ''} / ${p?.information?.title || ''}`,
                model: p?.modelType || '',
                plan: pf?.plan?.title || pf?.planTitle || '',
                reason: 'Project hidden'
            })
        }
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={760}
            title={(
                <span>
                    Hidden by Plan Filter{profile ? ` — ${profile}` : ''} ({rows.length} plan{rows.length === 1 ? '' : 's'})
                </span>
            )}
        >
            {rows.length === 0 ? (
                <div style={{color: '#888'}}>Nothing hidden.</div>
            ) : (
                <ResultTable style={{borderCollapse: 'collapse', width: '100%'}}>
                    <thead>
                        <tr>
                            <th style={TH_STYLE}>Project</th>
                            <th style={TH_STYLE}>Model</th>
                            <th style={TH_STYLE}>Plan</th>
                            <th style={TH_STYLE}>Reason</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => (
                            <tr key={idx}>
                                <td style={TD_STYLE}>{r.project}</td>
                                <td style={TD_STYLE}>{r.model}</td>
                                <td style={TD_STYLE}>{r.plan}</td>
                                <td style={TD_STYLE}>{r.reason}</td>
                            </tr>
                        ))}
                    </tbody>
                </ResultTable>
            )}
        </Modal>
    )
}

// Plans that exist on disk as `*.p01` etc. but aren't declared in the .prj.
// The uploader collects them into `unlistedPlanFiles`; we just surface them.
const UnlistedPlansModal = ({open, onClose, projects}) => {
    const rows = []
    for (const p of (projects || [])) {
        for (const u of (p?.unlistedPlanFiles || [])) {
            rows.push({
                project: `${p?.information?.folderName || ''} / ${p?.information?.title || ''}`,
                fileName: u?.fileName || '',
                ext: u?.ext || '',
                title: u?.title || '',
            })
        }
    }

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={760}
            title={(
                <span>
                    Plans not listed in .prj ({rows.length} plan{rows.length === 1 ? '' : 's'})
                </span>
            )}
        >
            {rows.length === 0 ? (
                <div style={{color: '#888'}}>Every plan file is referenced by its .prj.</div>
            ) : (
                <ResultTable style={{borderCollapse: 'collapse', width: '100%'}}>
                    <thead>
                        <tr>
                            <th style={TH_STYLE}>Project</th>
                            <th style={TH_STYLE}>Plan File</th>
                            <th style={TH_STYLE}>Plan Title</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r, idx) => (
                            <tr key={idx}>
                                <td style={TD_STYLE}>{r.project}</td>
                                <td style={TD_STYLE}>{r.fileName}</td>
                                <td style={TD_STYLE}>{r.title}</td>
                            </tr>
                        ))}
                    </tbody>
                </ResultTable>
            )}
        </Modal>
    )
}

export default function ProjectList() {
    const {appContext, dispatchProject, applyPlanFilter, clearPlanFilter} = useAppContext()
    const [tab, setTab] = useState(null)
    const planFilter = appContext?.planFilter
    const [filterProfile, setFilterProfile] = useState(planFilter?.profile || 'FW')
    const [hiddenModalOpen, setHiddenModalOpen] = useState(false)
    const [unlistedModalOpen, setUnlistedModalOpen] = useState(false)
    const tabsContainerRef = useRef(null)

    const unlistedPlanCount = (appContext?.projects || [])
        .reduce((sum, p) => sum + (p?.unlistedPlanFiles?.length || 0), 0)

    const filteredOutPlanCount =
        (appContext?.projects || []).reduce((sum, p) => sum + (p?.filteredOutFiles?.length || 0), 0) +
        (appContext?.filteredOutProjects || []).reduce((sum, p) => sum + (p?.projectFiles?.length || 0) + (p?.filteredOutFiles?.length || 0), 0)
    const filteredOutProjectCount = appContext?.filteredOutProjects?.length || 0
    const activeProjectCount = appContext?.projects?.length || 0
    const activePlanCount = (appContext?.projects || []).reduce((sum, p) => sum + (p?.projectFiles?.length || 0), 0)
    const activeRiverCount = (() => {
        const set = new Set()
        for (const p of appContext?.projects || []) {
            for (const pf of p?.projectFiles || []) {
                for (const r of pf?.rivers || []) {
                    if (r?.river) set.add(r.river)
                }
            }
        }
        return set.size
    })()
    const showFilterBar = (appContext?.projects?.length || 0) > 0 || planFilter?.active

    const moveTab = (delta) => {
        const ids = (appContext?.projects || []).map(p => p?.information?.id)
        if(ids.length === 0)
            return
        const currentIndex = ids.indexOf(tab)
        const base = currentIndex < 0 ? 0 : currentIndex
        const nextIndex = (base + delta + ids.length) % ids.length
        setTab(ids[nextIndex])
    }

    useEffect(() => {
        const onKeyDown = (e) => {
            const target = e.target
            const tag = (target?.tagName || '').toLowerCase()
            if(target?.isContentEditable || tag === 'input' || tag === 'textarea' || tag === 'select')
                return

            if(e.key === 'ArrowDown') {
                e.preventDefault()
                moveTab(1)
            }
            else if(e.key === 'ArrowUp') {
                e.preventDefault()
                moveTab(-1)
            }
        }

        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [tab, appContext?.projects])

    useEffect(() => {
        if(!tab && appContext?.projects?.length > 0) {
            setTab(appContext?.projects[0]?.information?.id)
        }
    }, [appContext?.projects?.length]);

    // Keep the active tab pointing at a project that still exists (filtering
    // can hide entire projects).
    useEffect(() => {
        const ids = (appContext?.projects || []).map(p => p?.information?.id)
        if(ids.length === 0) {
            if(tab !== null) setTab(null)
        }
        else if(!ids.includes(tab)) {
            setTab(ids[0])
        }
    }, [appContext?.projects]);

    const onProjectChanged = (project) => {
        dispatchProject({
            type: AppActions.SET_PROJECT,
            data: {
                project: {
                    ...project
                }
            }
        })
    }

    const onSaveProject = async (project) => {
        const input = {
            title: project?.information?.title?.trim(),
            description: project?.information?.descriptions || [],
            planCount: project?.projectFiles?.length,
            projectState: await compressJsonBase64(project),
            key: project?.key,
            notes: project?.notes,
            hecrasVersion: project?.version
        }

        const res = await ApiService.UserProject.SaveProject(input)
        if(res.success) {
            notification.success({
                message: 'Project was saved successfully',
                placement: 'bottomRight',
            })
        }
    }

    const onRemoved = (projectId) => {
        const index = appContext?.projects?.findIndex(x => x?.information?.id === projectId)
        if(index > 0) {
            setTab(appContext?.projects[index-1]?.information?.id)
        }

        dispatchProject({
            type: AppActions.REMOVE_PROJECT,
            data: {id: projectId}
        })
    }

    useEffect(() => {
        if(appContext?.projects?.length === 1) {
            setTab(appContext?.projects[0]?.information?.id)
        }
    }, [appContext?.projects?.length]);

    // Mouse-wheel inside the nav: antd's own wheel handler on
    // `.ant-tabs-nav-wrap` calls preventDefault to drive its (now-disabled)
    // transform scroll, swallowing the event before the browser can scroll
    // our nav. Attach a capture-phase listener so we run *before* antd,
    // stop its handler from seeing the event, and translate deltaY into
    // scrollTop on the nav ourselves.
    useEffect(() => {
        const container = tabsContainerRef.current
        if(!container) return
        const nav = container.querySelector('.ant-tabs-nav')
        if(!nav) return

        const onWheel = (e) => {
            const delta = e.deltaY || e.deltaX
            if(!delta) return
            nav.scrollTop += delta
            e.preventDefault()
            e.stopPropagation()
        }
        nav.addEventListener('wheel', onWheel, {passive: false, capture: true})
        return () => nav.removeEventListener('wheel', onWheel, {capture: true})
    }, [appContext?.projects?.length])

    // Keep the active tab in view inside the (now CSS-scrolled) nav. Antd's
    // own scroll mechanics are disabled in index.css, so we drive the scroll
    // ourselves whenever the selection changes (keyboard, button, or click).
    // We don't use scrollIntoView because the page <html> is also scrollable
    // (overflow-y: scroll in index.css) and that would scroll the page too.
    // Manual scrollTop keeps the page still and only moves the nav.
    useEffect(() => {
        if(!tab) return
        const raf = requestAnimationFrame(() => {
            const container = tabsContainerRef.current
            if(!container) return
            const nav = container.querySelector('.ant-tabs-nav')
            const active = container.querySelector('.ant-tabs-tab-active')
            if(!nav || !active) return

            const navRect = nav.getBoundingClientRect()
            const activeRect = active.getBoundingClientRect()
            if(activeRect.top < navRect.top) {
                nav.scrollTop -= (navRect.top - activeRect.top)
            }
            else if(activeRect.bottom > navRect.bottom) {
                nav.scrollTop += (activeRect.bottom - navRect.bottom)
            }
        })
        return () => cancelAnimationFrame(raf)
    }, [tab]);

    const renderTabs = () => {
        return appContext?.projects?.map(x => ({
            label: (
                <table key={x?.information?.id}>
                    <tbody>
                    <tr>
                        <td style={{textAlign: 'start'}}>
                            <div style={{width: 300, whiteSpace: 'normal', wordBreak: 'break-word'}}>
                                <b style={{color: hasOutputError(x) ? '#ff0000' : undefined}}>
                                    {x?.information?.folderName} / {x?.information?.title}
                                </b>
                            </div>
                        </td>
                        <td>
                            <Tag>{x?.modelType}</Tag>
                            <Tooltip title={'Plan file count in this project'}>
                                <Tag color={(x?.projectFiles?.length || 0) > 1 ? 'red' : 'blue'}>{x?.projectFiles?.length || 0} plan</Tag>
                            </Tooltip>
                        </td>
                        <td>
                            <Button
                                style={{width: 32}}
                                size={'small'}
                                type={'text'}
                                icon={<CloseOutlined style={{marginRight: 0}}/>}
                                onClick={(e) => {
                                    e.preventDefault()
                                    e.stopPropagation()
                                    onRemoved(x?.information?.id)
                                }}
                            />
                        </td>
                    </tr>
                    </tbody>
                </table>
            ),
            key: x?.information?.id,
            children: (
                <div key={`${x?.information?.id}__${appContext?.planFilter?.version || 0}`}>
                    <ProjectTab
                        project={x}
                        onSaveProject={onSaveProject}
                        onRemoved={onRemoved}
                        onProjectChanged={onProjectChanged}
                        allProjects={appContext?.projects}
                    />
                </div>
            )
        }))
    }

    // Tab bar scroll is now handled by CSS on `.project-list-tabs > .ant-tabs-nav`
    // (see index.css). Setting tabBarStyle here would conflict with antd's own
    // virtual-scroll chevrons and re-introduce the jitter.

    return (
        <>
            {showFilterBar && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        flexWrap: 'wrap',
                        marginBottom: 12,
                        padding: '8px 12px',
                        background: '#fafafa',
                        border: '1px solid #f0f0f0',
                        borderRadius: 4
                    }}
                >
                    <Tooltip title={'Active projects, plans and unique rivers (after filtering)'}>
                        <Tag color={'geekblue'} style={{margin: 0}}>
                            {activeProjectCount} project{activeProjectCount === 1 ? '' : 's'} · {activePlanCount} plan{activePlanCount === 1 ? '' : 's'} · {activeRiverCount} river{activeRiverCount === 1 ? '' : 's'}
                        </Tag>
                    </Tooltip>
                    <Tooltip title={'Previous project (Up arrow)'}>
                        <Button
                            size={'small'}
                            icon={<ArrowUpOutlined/>}
                            onClick={() => moveTab(-1)}
                            disabled={activeProjectCount === 0}
                        />
                    </Tooltip>
                    <Tooltip title={'Next project (Down arrow)'}>
                        <Button
                            size={'small'}
                            icon={<ArrowDownOutlined/>}
                            onClick={() => moveTab(1)}
                            disabled={activeProjectCount === 0}
                        />
                    </Tooltip>
                    <FilterOutlined/>
                    <b>Plan Filter</b>
                    <span>Keep only plans that have the</span>
                    <Select
                        size={'small'}
                        style={{width: 140}}
                        value={filterProfile}
                        onChange={setFilterProfile}
                    >
                        {PlanProfiles.map(p => (
                            <Select.Option key={p.value} value={p.value}>{p.visible_label || p.label}</Select.Option>
                        ))}
                    </Select>
                    <span>profile</span>
                    <Tooltip title={'Applies to every plan of every model. Filtered-out plans are kept aside — use Rollback to bring them all back.'}>
                        <Button
                            size={'small'}
                            type={'primary'}
                            icon={<FilterOutlined/>}
                            onClick={() => applyPlanFilter(filterProfile)}
                        >
                            {planFilter?.active ? 'Re-Filter' : 'Filter Plans'}
                        </Button>
                    </Tooltip>
                    {unlistedPlanCount > 0 && (
                        <Tooltip title={'Plan files on disk that are not referenced by the project (.prj) — they were excluded from this run.'}>
                            <Button
                                size={'small'}
                                danger
                                icon={<ExclamationCircleOutlined/>}
                                onClick={() => setUnlistedModalOpen(true)}
                            >
                                {unlistedPlanCount} unlisted
                            </Button>
                        </Tooltip>
                    )}
                    {planFilter?.active && (
                        <Button
                            size={'small'}
                            danger
                            icon={<UndoOutlined/>}
                            onClick={() => clearPlanFilter()}
                        >
                            Rollback
                        </Button>
                    )}
                    {planFilter?.active && (
                        <span style={{color: '#888'}}>
                            Filtering by <b>{planFilter.profile}</b> — {filteredOutPlanCount} plan(s)
                            {filteredOutProjectCount > 0 ? ` across ${filteredOutProjectCount} project(s)` : ''} hidden
                        </span>
                    )}
                    {planFilter?.active && (filteredOutPlanCount > 0 || filteredOutProjectCount > 0) && (
                        <Tooltip title={'See which plans and projects are hidden'}>
                            <Button
                                size={'small'}
                                type={'default'}
                                icon={<InfoCircleOutlined/>}
                                onClick={() => setHiddenModalOpen(true)}
                            >
                                Details
                            </Button>
                        </Tooltip>
                    )}
                </div>
            )}
            <HiddenItemsModal
                open={hiddenModalOpen}
                onClose={() => setHiddenModalOpen(false)}
                projects={appContext?.projects || []}
                filteredOutProjects={appContext?.filteredOutProjects || []}
                profile={planFilter?.profile}
            />
            <UnlistedPlansModal
                open={unlistedModalOpen}
                onClose={() => setUnlistedModalOpen(false)}
                projects={appContext?.projects || []}
            />
            <div ref={tabsContainerRef}>
                <Tabs
                    className={'project-list-tabs'}
                    type={'card'}
                    tabPosition={'left'}
                    onChange={(tab) => {
                        setTab(tab)
                    }}
                    activeKey={tab}
                    items={renderTabs() || []}
                />
            </div>
        </>
    )
}